import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Download, FileText, Send, ArrowUpDown, Receipt, CalendarPlus, PenLine, Link2, Clock, MailCheck, CheckCircle2 } from 'lucide-react';
import api from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import EmptyState from '../components/EmptyState';
import { SkeletonRow } from '../components/Skeleton';
import BulkActionsBar from '../components/BulkActionsBar';
import { exportToCsv, downloadPdf } from '../utils/export';
import InlineStatus from '../components/InlineStatus';
import { toast } from '../hooks/use-toast';
import { useLastSeen, useAutoMarkSeen } from '../hooks/useLastSeen';
import { useBulkSelectionKeyboard } from '../hooks/useBulkSelectionKeyboard';
import QuoteFormDialog from '../components/QuoteFormDialog';
import SignaturePad from '../components/SignaturePad';
import StatusPipeline from '../components/StatusPipeline';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import DevisKPICards from '../components/devis/DevisKPICards';
import DevisToolbar from '../components/devis/DevisToolbar';
import ConventionAction from '../components/devis/ConventionAction';
import QuoteLearnersPopover from '../components/devis/QuoteLearnersPopover';
import QuoteVersionsTimeline from '../components/devis/QuoteVersionsTimeline';
import PeriodFilter, { periodCutoffDate } from '../components/PeriodFilter';

const QUOTE_STATUSES = ['Brouillon', 'Envoyé', 'Accepté', 'Négociation', 'Refusé', 'Converti'];

const STATUS_STYLES = {
  'Accepté': 'bg-[#E6FBF7] text-[#0F766E]',
  'Envoyé': 'bg-[#E0F2FE] text-[#0369A1]',
  'Brouillon': 'bg-[#F3F4F8] text-[#4A4B63]',
  'Négociation': 'bg-[#FEF3E7] text-[#B45309]',
  'Refusé': 'bg-[#FEF2F2] text-[#B91C1C]',
  'Converti': 'bg-[#EEF2FF] text-[#4F46E5]',
};

export default function Devis() {
  useAutoMarkSeen('quotes_pending', 3000);
  const { isNew, markSeen } = useLastSeen();
  // Locally dismissed NOUVEAU badges — persisted to localStorage so dismissal
  // survives reloads/navigation. Server-side `markSeen` is still called so
  // other browsers/users/devices see the counter reset.
  // SAFE: stores ONLY non-sensitive UI state — array of quote IDs the user
  // has dismissed (used to hide the "NOUVEAU" badge across reloads). No
  // credentials, tokens, or PII. Auth tokens live in sessionStorage (XSS
  // hardening, Vague 97.x).
  const LS_KEY = 'ifaap_devis_seen_ids';
  const [locallySeen, setLocallySeen] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const markLocalSeen = (id) => setLocallySeen((prev) => {
    if (prev.has(id)) return prev;
    const n = new Set(prev); n.add(id);
    try { localStorage.setItem(LS_KEY, JSON.stringify(Array.from(n).slice(-500))); } catch { /* ignore */ }
    return n;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [prefillCreate, setPrefillCreate] = useState(null);
  const [search, setSearch] = useState('');
  const [devis, setDevis] = useState([]);
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [kpis, setKpis] = useState(null);
  const [signing, setSigning] = useState(null); // quote being signed
  const [history, setHistory] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkSignOpen, setBulkSignOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // Advanced filters
  const [filterStatuses, setFilterStatuses] = useState(new Set());
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');
  // Vague 99.4 — Quick period filter (30j / 90j / 12m / all). Recomputes KPIs.
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    api.get('/quotes').then(({ data }) => setDevis(data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/analytics/quotes-kpis').then(({ data }) => setKpis(data)).catch(() => {});
  }, []);

  // Vague 99.2 — Deep-link support: /app/devis?focus=ID opens the detail,
  // and /app/devis?status=Envoyé presets the status filter pill.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const status = params.get('status');
    if (status) {
      setFilterStatuses(new Set([status]));
    }
  }, [location.search]);

  useEffect(() => {
    if (!devis.length) return;
    const params = new URLSearchParams(location.search || '');
    const fid = params.get('focus');
    if (!fid) return;
    const found = devis.find((d) => d.id === fid);
    if (found) {
      setDetail(found);
      navigate(location.pathname, { replace: true });
    }
  }, [devis, location.search, location.pathname, navigate]);

  // Auto-open "Nouveau devis" with company prefilled when navigated from fiche entreprise
  useEffect(() => {
    const prefill = location.state?.prefillCreate;
    if (prefill) {
      setPrefillCreate(prefill);
      setCreate(true);
      // Clear navigation state so a refresh doesn't re-trigger the dialog
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  // Re-fetch KPIs when quotes state changes meaningfully
  const refreshKpis = () => api.get('/analytics/quotes-kpis').then(({ data }) => setKpis(data)).catch(() => {});

  // Load history + versions when opening detail sheet
  useEffect(() => {
    if (detail?.id) {
      api.get(`/quotes/${detail.id}/history`).then(({ data }) => setHistory(data)).catch(() => setHistory({ audits: [], signatures: [] }));
      api.get(`/quotes/${detail.id}/versions`).then(({ data }) => setVersions(data)).catch(() => setVersions([]));
    } else {
      setVersions([]);
    }
  }, [detail?.id]);

  const handleDownloadVersionPdf = async (version) => {
    try {
      const res = await api.get(`/quotes/${detail.id}/versions/${version}/pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      toast({ title: 'Erreur', description: 'PDF de version indisponible' });
    }
  };

  const handleRestoreVersion = async (version) => {
    try {
      const { data } = await api.post(`/quotes/${detail.id}/versions/${version}/restore`);
      setDevis((prev) => prev.map((x) => x.id === data.id ? data : x));
      setDetail(data);
      // Refresh versions timeline
      api.get(`/quotes/${data.id}/versions`).then(({ data: v }) => setVersions(v)).catch(() => {});
      refreshKpis();
      toast({ title: 'Version restaurée', description: `Le devis a été ramené à la version ${version}.` });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Restauration impossible' });
    }
  };

  const handleSignAdmin = async (quote, signatureB64, signatoryName) => {
    try {
      await api.post(`/quotes/${quote.id}/sign-admin`, { signatory_name: signatoryName, signature_b64: signatureB64 });
      setDevis((list) => list.map((x) => x.id === quote.id ? { ...x, status: 'Accepté', signed_at: new Date().toISOString(), signatory_name: signatoryName } : x));
      toast({ title: 'Devis signé', description: `${quote.id} → Accepté` });
      setSigning(null);
      refreshKpis();
      if (detail?.id === quote.id) setDetail({ ...detail, status: 'Accepté' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible de signer' });
    }
  };

  const handleCreateSignLink = async (quote) => {
    try {
      const { data } = await api.post(`/quotes/${quote.id}/signature-link`);
      try { await navigator.clipboard.writeText(data.full_url || (window.location.origin + data.url)); } catch { /* ignore */ }
      if (data.email_sent) {
        toast({
          title: 'Email envoyé au client',
          description: `${data.email_to} · lien valide jusqu'au ${new Date(data.expires_at).toLocaleDateString('fr-FR')}`,
        });
      } else if (data.email_to) {
        toast({
          title: 'Lien copié — email non envoyé',
          description: data.email_error
            ? `${data.email_error} (destinataire prévu : ${data.email_to}). Le lien a été copié dans le presse-papier.`
            : `Destinataire prévu : ${data.email_to}. Vérifiez votre configuration email (Paramètres > Emails). Le lien a été copié dans le presse-papier.`,
        });
      } else {
        toast({
          title: 'Lien copié dans le presse-papier',
          description: 'Aucun email client renseigné — envoyez le lien manuellement.',
        });
      }
      setDevis((list) => list.map((x) => {
        if (x.id !== quote.id) return x;
        const patch = { ...x, status: x.status === 'Brouillon' ? 'Envoyé' : x.status };
        if (data.email_sent) {
          patch.email_sent_at = new Date().toISOString();
          patch.email_sent_to = data.email_to || x.email_sent_to;
        }
        return patch;
      }));
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible de générer le lien' });
    }
  };

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const handleDelete = async () => {
    try {
      await api.delete(`/quotes/${deleting.id}`);
      setDevis((prev) => prev.filter((d) => d.id !== deleting.id));
      toast({ title: 'Devis supprimé' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({ title: 'Suppression impossible', description: typeof detail === 'string' ? detail : 'Erreur inattendue' });
    }
  };

  // L'étude de besoin part **avant le devis**, pour valider les noms des
  // apprenants et ce qu'on sait du client. C'est donc elle qu'on joint pour
  // montrer sur quoi le chiffrage repose — et comme une entreprise peut en
  // porter plusieurs, une par formation chiffrée, l'écran demande laquelle
  // plutôt que de joindre « la dernière », qui serait juste une fois sur deux.
  // { devis, etudes, modes: { [id]: 'pdf' | 'lien' | null } }
  const [aJoindre, setAJoindre] = useState(null);

  const etudesDuClient = async (d) => {
    if (!d.company_id) return [];
    try {
      const { data } = await api.get(`/companies/${d.company_id}/questionnaires`);
      // Toutes, pas seulement les remplies : une étude faite se joint en
      // PDF, une étude à faire se joint en lien. Les deux moments existent.
      return (data || []).filter((q) => q.kind === 'needs_analysis');
    } catch {
      return [];   // une étude introuvable n'empêche pas d'envoyer le devis
    }
  };

  const handleSend = async (d) => {
    try {
      if (!d.client_email) {
        toast({ title: 'Email client manquant', description: 'Renseignez d\'abord l\'email du client sur ce devis.' });
        return;
      }
      const isResend = !!d.email_sent_at;
      // S'il y a une étude de besoin remplie chez ce client, on demande avant
      // d'envoyer — un clic de plus, et seulement quand il y a quelque chose à
      // joindre.
      if (!d._etudesDemandees) {
        const etudes = await etudesDuClient(d);
        if (etudes.length) {
          // Faite : son PDF. Pas faite : son lien. C'est ce que l'état dit
          // déjà, et ça reste modifiable d'un clic.
          const modes = {};
          etudes.forEach((e, i) => {
            modes[e.id] = i === 0 ? (e.status === 'filled' ? 'pdf' : 'lien') : null;
          });
          setAJoindre({ devis: d, etudes, modes });
          return;
        }
      }
      const { data } = await api.post(
        `/quotes/${d.id}/signature-link?attach_pdf=true`,
        { etudes: d._etudes || null },
      );
      if (d.status === 'Brouillon') {
        const upd = await api.put(`/quotes/${d.id}`, { ...d, status: 'Envoyé' });
        setDevis((prev) => prev.map((x) => x.id === upd.data.id ? upd.data : x));
      }
      if (data.email_sent) {
        const newCount = Number(data.email_sent_count || (Number(d.email_sent_count || 0) + 1));
        setDevis((prev) => prev.map((x) => x.id === d.id ? {
          ...x,
          email_sent_at: new Date().toISOString(),
          email_sent_to: data.email_to || x.email_sent_to,
          email_sent_count: newCount,
        } : x));
        toast({
          title: isResend ? `Devis renvoyé (${newCount}×) ✅` : 'Devis envoyé par email ✅',
          description: `Devis PDF + lien de signature envoyés à ${data.email_to}`,
        });
      } else {
        toast({
          title: 'Envoi email échoué',
          description: data.email_error
            ? `${data.email_error} — Lien de secours : ${data.full_url}`
            : `Aucune configuration SMTP active. Configurez votre boîte email dans Paramètres > Emails. Lien de secours : ${data.full_url}`,
        });
      }
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast({ title: 'Erreur envoi devis', description: typeof det === 'string' ? det : 'Vérifiez la configuration email dans Paramètres.' });
    }
  };

  const handleDownload = async (d) => {
    try {
      await downloadPdf(api, `/quotes/${d.id}/pdf`, `devis-${d.id}`);
      toast({ title: 'PDF téléchargé', description: `devis-${d.id}.pdf` });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de générer le PDF.' });
    }
  };

  const handleConvert = async (d) => {
    if (d.status !== 'Accepté') return;
    try {
      const { data } = await api.post(`/quotes/${d.id}/convert-to-invoice`);
      // Mark the quote as Converted locally
      setDevis((prev) => prev.map((x) => x.id === d.id ? { ...x, status: 'Converti', converted_to_invoice_id: data.invoice.id } : x));
      toast({
        title: data.already_converted ? 'Déjà converti' : 'Facture créée',
        description: `${data.invoice.id} · ${data.invoice.amount.toLocaleString('fr-FR')} € TTC`,
      });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Conversion impossible' });
    }
  };

  const handleConvertToSession = async (d, preinscription = false) => {
    if (d.status !== 'Accepté' && d.status !== 'Converti') {
      toast({ title: 'Devis non accepté', description: "Seuls les devis acceptés peuvent générer une session." });
      return;
    }
    try {
      const { data } = await api.get(`/quotes/${d.id}/session-payload`);
      const programme = data.programme || {};
      const snap = data.session_snapshot || {};
      const learnerIds = (data.learners || []).map((l) => l.id);
      // Default session dates: if the quote linked a session, use those.
      // Otherwise, fall back to the quote's signed_at (or creation date) to
      // give the user a sensible starting point they can still adjust.
      const fallbackStart = data.signed_at ? String(data.signed_at).slice(0, 10) : (d.date || '');
      const startDate = snap.startDate || snap.start_date || fallbackStart || '';
      // If no endDate, default to startDate + duration (assume ~7h = 1 day)
      let endDate = snap.endDate || snap.end_date || '';
      if (!endDate && startDate && (snap.duration || programme.duration)) {
        const days = Math.max(1, Math.ceil((snap.duration || programme.duration) / 7));
        const d2 = new Date(startDate);
        d2.setDate(d2.getDate() + (days - 1));
        endDate = d2.toISOString().slice(0, 10);
      }
      // Merge priority: session_snapshot > programme defaults > quote
      const prefill = {
        title: snap.title || programme.title || d.program || '',
        program: programme.title || d.program || '',
        programme_id: programme.id || '',
        trainer: snap.trainer || programme.trainer || '',
        duration: snap.duration || programme.duration || 0,
        mode: snap.mode || programme.mode || 'Présentiel',
        location: snap.location || programme.location || '',
        description: programme.description || '',
        objectives: programme.objectives || '',
        prerequisites: programme.prerequisites || '',
        public_cible: programme.public_cible || '',
        price: programme.price || 0,
        programme_pdf_url: programme.programme_pdf_url || '',
        nsf_code: programme.nsf_code || '',
        startDate,
        endDate,
        capacity: Math.max(learnerIds.length || 0, 10),
        description: [
          programme.description || '',
          `\n\n— Créée à partir du devis ${data.quote_number || d.id}${data.signed_at ? ` (signé le ${new Date(data.signed_at).toLocaleDateString('fr-FR')})` : ''}.`,
          data.notes ? `Notes du devis : ${data.notes}` : '',
        ].filter(Boolean).join(''),
        status: preinscription ? 'Préinscription' : 'Ouverte',
      };
      navigate('/app/sessions', {
        state: {
          fromDevis: true,
          sessionPrefill: prefill,
          preSelectedLearnerIds: learnerIds,
          preinscription,
          lockedFromQuote: true,
          quoteNumber: data.quote_number || d.id,
          // Existing session linked on the devis — when present, finalizing
          // the pré-inscription should just enroll the learners into it
          // instead of creating a duplicate session.
          existingSessionId: snap.id || null,
          // Company tied to the quote — used to restrict the pré-inscription
          // dialog to learners of this company only (RGPD/cohérence).
          companyId: data.company_id || null,
          companyName: d.client || '',
        },
      });
      toast({
        title: 'Session pré-remplie depuis le devis',
        description: learnerIds.length
          ? `${learnerIds.length} apprenant(s) pré-sélectionné(s) · programme, dates et tarifs repris du devis signé.`
          : "Aucun apprenant rattaché — vous pouvez en ajouter dans le formulaire.",
      });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de préparer la session.' });
    }
  };

  const filtered = useMemo(() => {
    const cutoff = periodCutoffDate(period);
    const arr = devis.filter((d) => {
      // Text search
      if (search && !`${d.id} ${d.client} ${d.program}`.toLowerCase().includes(search.toLowerCase())) return false;
      // Multi-status
      if (filterStatuses.size > 0 && !filterStatuses.has(d.status)) return false;
      // Quick period filter (Vague 99.4)
      if (cutoff && d.date && d.date < cutoff) return false;
      // Date range (uses the `date` field = émission)
      if (filterDateFrom && d.date && d.date < filterDateFrom) return false;
      if (filterDateTo && d.date && d.date > filterDateTo) return false;
      // Amount range (HT)
      const amt = Number(d.amount) || 0;
      if (filterMinAmount && amt < Number(filterMinAmount)) return false;
      if (filterMaxAmount && amt > Number(filterMaxAmount)) return false;
      return true;
    });
    return arr.sort((a, b) => {
      const va = a[sort.key] ?? ''; const vb = b[sort.key] ?? '';
      if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
      return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [devis, search, sort, filterStatuses, filterDateFrom, filterDateTo, filterMinAmount, filterMaxAmount, period]);

  // Vague 99.4 — Local KPIs derived from `filtered` so the period chip
  // changes the KPI cards in real time (no extra API call).
  const localKpis = useMemo(() => {
    const total = filtered.length;
    const accepted = filtered.filter((d) => ['Accepté', 'Converti'].includes(d.status));
    const pending = filtered.filter((d) => ['Brouillon', 'Envoyé', 'Négociation'].includes(d.status));
    const sumAmount = (arr) => arr.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    return {
      pipeline_amount: sumAmount(pending),
      accepted_amount: sumAmount(accepted),
      acceptance_rate: total > 0 ? Math.round((accepted.length / total) * 100) : 0,
      avg_conversion_days: kpis?.avg_conversion_days, // keep server value (computed across full history)
    };
  }, [filtered, kpis]);
  // Use local KPIs when a period filter is active (period !== 'all'), else use server-side KPIs.
  const displayKpis = period !== 'all' ? localKpis : (kpis || localKpis);

  useBulkSelectionKeyboard({
    items: filtered,
    selected,
    setSelected,
    onDelete: () => setBulkDeleteOpen(true),
  });

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = (ids) => setSelected((s) => s.size === ids.length ? new Set() : new Set(ids));

  const runBulkSignatureLink = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast({ title: 'Aucun devis sélectionné' }); throw new Error(); }
    try {
      const { data } = await api.post('/quotes/bulk-signature-link', { ids });
      const sent = (data.results || []).filter((r) => r.email_sent).length;
      const noEmail = (data.results || []).filter((r) => !r.email_to).length;
      const errors = (data.results || []).filter((r) => r.error).length;
      toast({
        title: 'Liens de signature générés',
        description: `${sent} email${sent > 1 ? 's' : ''} envoyé${sent > 1 ? 's' : ''}${noEmail ? ` · ${noEmail} sans email` : ''}${errors ? ` · ${errors} erreur${errors > 1 ? 's' : ''}` : ''}`,
      });
      // refresh to surface updated status (Brouillon → Envoyé)
      const { data: fresh } = await api.get('/quotes');
      setDevis(fresh);
      setSelected(new Set());
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Génération groupée impossible' });
      throw new Error();
    }
  };

  const bulkExport = () => {
    const rows = devis.filter((d) => selected.has(d.id));
    exportToCsv('devis-selection', rows, ['id', 'client', 'program', 'amount', 'date', 'validUntil', 'status']);
    toast({ title: 'Export CSV', description: `${rows.length} devis` });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected).filter((id) => {
      const q = devis.find((x) => x.id === id);
      return q && !['Accepté', 'Converti'].includes(q.status);
    });
    if (ids.length === 0) { toast({ title: 'Suppression refusée', description: 'Aucun devis modifiable dans la sélection (signés/convertis sont verrouillés).' }); throw new Error(); }
    try {
      await api.post('/bulk/delete', { collection: 'quotes', ids });
      setDevis((prev) => prev.filter((x) => !ids.includes(x.id)));
      setSelected(new Set());
      refreshKpis();
      toast({ title: `${ids.length} devis supprimé${ids.length > 1 ? 's' : ''}` });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible' });
      throw new Error();
    }
  };

  const total = devis.reduce((s, d) => s + d.amount, 0);
  const accepte = devis.filter((d) => d.status === 'Accepté').reduce((s, d) => s + d.amount, 0);
  const accepRate = devis.length ? Math.round((devis.filter((d) => d.status === 'Accepté').length / devis.length) * 100) : 0;

  const SortTh = ({ label, k }) => (
    <th className="py-3.5 px-5 cursor-pointer select-none" onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className={`w-3 h-3 ${sort.key === k ? 'text-[#4F46E5]' : 'text-[#D4D5E0]'}`} /></span>
    </th>
  );

  return (
    <div className="space-y-5 flex flex-col h-[calc(100vh-72px)]">
      <DevisKPICards kpis={displayKpis} />

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[12px] font-semibold text-[#6B6C85]">Période :</span>
        <PeriodFilter value={period} onChange={setPeriod} testId="devis-period" />
      </div>

      <DevisToolbar
        search={search}
        setSearch={setSearch}
        statuses={QUOTE_STATUSES}
        statusStyles={STATUS_STYLES}
        filterStatuses={filterStatuses}
        setFilterStatuses={setFilterStatuses}
        filterDateFrom={filterDateFrom}
        setFilterDateFrom={setFilterDateFrom}
        filterDateTo={filterDateTo}
        setFilterDateTo={setFilterDateTo}
        filterMinAmount={filterMinAmount}
        setFilterMinAmount={setFilterMinAmount}
        filterMaxAmount={filterMaxAmount}
        setFilterMaxAmount={setFilterMaxAmount}
        filteredCount={filtered.length}
        totalCount={devis.length}
        filtered={filtered}
        onCreate={() => setCreate(true)}
      />

      {/* Vague 99.4 — Tableau scrollable, hauteur limitée à l'écran. */}
      <div className="soft-card overflow-hidden flex-1 min-h-0 flex flex-col" data-testid="devis-table-wrap">
        <div className="overflow-y-auto flex-1 min-h-0">
        <table className="w-full">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] border-b border-[#EFF0F6]">
              <th className="py-3.5 pl-5 pr-1 w-10">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onChange={() => toggleSelectAll(filtered.map((d) => d.id))}
                  data-testid="devis-select-all"
                  className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer"
                />
              </th>
              <SortTh label="N° Devis" k="id" />
              <SortTh label="Client" k="client" />
              <th className="py-3.5 px-5">Programme</th>
              <SortTh label="Montant HT" k="amount" />
              <SortTh label="Date" k="date" />
              <SortTh label="Statut" k="status" />
              <th className="py-3.5 px-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={8} />)}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8}><EmptyState icon={FileText} title="Aucun devis" description="Créez votre premier devis pour commencer." action={<button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">Nouveau devis</button>} /></td></tr>
            )}
            {!loading && filtered.map((d) => {
              const itemIsNew = isNew('quotes_pending', d.created_at) && !locallySeen.has(d.id);
              return (
              <tr key={d.id} onClick={() => { markLocalSeen(d.id); markSeen('quotes_pending'); setDetail(d); }} className={`group data-row border-b border-[#F3F4F8] last:border-0 cursor-pointer ${selected.has(d.id) ? 'bg-[#EEF2FF]' : d.status === 'Converti' ? 'bg-[#FAFAFE] opacity-70' : itemIsNew ? 'bg-[#FAF6FF] hover:bg-[#EEF2FF]' : ''}`}>
                <td className="py-4 pl-5 pr-1 w-10" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(d.id)}
                    onChange={() => toggleSelect(d.id)}
                    data-testid={`devis-select-${d.id}`}
                    className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer"
                  />
                </td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center">
                      <FileText className="w-4 h-4 text-[#4F46E5]" />
                    </div>
                    <span className="text-[13px] font-mono font-semibold text-[#1A1B2E]">{d.id}</span>
                    {itemIsNew && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#4F46E5] text-white text-[9.5px] font-bold tracking-wide">NOUVEAU</span>}
                  </div>
                </td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-semibold text-[#1A1B2E]">{d.client}</span>
                    <QuoteLearnersPopover
                      id={d.id}
                      learnerIds={d.learner_ids}
                      learnerNames={d.learner_names}
                      testIdPrefix="devis"
                    />
                  </div>
                </td>
                <td className="py-4 px-5 text-[13px] text-[#4A4B63]">{d.program}</td>
                <td className="py-4 px-5 text-[13.5px] font-bold text-[#1A1B2E]">{d.amount?.toLocaleString('fr-FR')} €</td>
                <td className="py-4 px-5 text-[13px] text-[#4A4B63]">{d.date && !isNaN(new Date(d.date).getTime()) ? new Date(d.date).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="py-4 px-5">
                  <InlineStatus
                    testId={`status-quote-${d.id}`}
                    value={d.status}
                    options={QUOTE_STATUSES}
                    styles={STATUS_STYLES}
                    onChange={async (newStatus) => {
                      const prev = d.status;
                      setDevis((list) => list.map((x) => x.id === d.id ? { ...x, status: newStatus } : x));
                      try {
                        await api.put(`/quotes/${d.id}`, { ...d, status: newStatus });
                        toast({ title: 'Statut devis', description: `${d.id} → ${newStatus}` });
                      } catch (err) {
                        setDevis((list) => list.map((x) => x.id === d.id ? { ...x, status: prev } : x));
                        const detail = err?.response?.data?.detail;
                        if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
                          toast({ title: detail.message || 'Incohérences bloquantes', description: detail.errors.slice(0, 3).join(' · ') });
                        } else {
                          toast({ title: 'Erreur', description: typeof detail === 'string' ? detail : 'Changement de statut refusé' });
                        }
                        throw new Error();
                      }
                    }}
                  />
                </td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-1 justify-end flex-nowrap" onClick={(e) => e.stopPropagation()}>
                    {d.status === 'Converti' && (() => {
                      // Wave 1u9e — badge reflects the linked invoice status so the user
                      // knows whether the quote is locked (Validée/Émise/Payée) or still
                      // editable (Provisoire = invoice draft awaiting validation).
                      const invStatus = d.converted_invoice_status || 'Provisoire';
                      const isFinal = ['Validée', 'Émise', 'Payée'].includes(invStatus);
                      const label = isFinal ? `Facture ${invStatus.toLowerCase()}` : 'Facture en attente';
                      const tip = isFinal
                        ? `${label} — devis verrouillé`
                        : `${label} (Provisoire) — devis encore modifiable jusqu'à validation`;
                      return (
                        <span
                          data-testid={`devis-locked-${d.id}`}
                          title={tip}
                          className={`inline-flex items-center gap-1 px-2 h-8 rounded-lg text-[11px] font-semibold ${isFinal ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-[#FEF3C7] text-[#92400E]'}`}
                        >
                          <Receipt className="w-3.5 h-3.5" /> {label}
                        </span>
                      );
                    })()}
                    {d.status === 'Accepté' && (() => {
                      const done = !!d.preinscription_at;
                      if (done) {
                        const sid = d.preinscription_session_id || '';
                        const tip = `Pré-inscription créée le ${new Date(d.preinscription_at).toLocaleDateString('fr-FR')}${sid ? ` — clic pour ouvrir la session ${sid}` : ''}`;
                        return (
                          <button
                            data-testid={`devis-to-session-${d.id}`}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); if (sid) navigate(`/app/sessions?focus=${sid}`); }}
                            title={tip}
                            className="w-8 h-8 rounded-lg bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC] flex items-center justify-center transition-colors"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        );
                      }
                      return (
                        <button
                          data-testid={`devis-to-session-${d.id}`}
                          onClick={() => handleConvertToSession(d, true)}
                          className="w-8 h-8 rounded-lg hover:bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center"
                          title="Créer une préinscription (session)"
                        >
                          <CalendarPlus className="w-3.5 h-3.5" />
                        </button>
                      );
                    })()}
                    {!['Accepté','Converti','Refusé'].includes(d.status) && (
                      <button data-testid={`devis-sign-${d.id}`} onClick={() => setSigning(d)} className="w-8 h-8 rounded-lg hover:bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center" title="Faire signer maintenant">
                        <PenLine className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {!['Accepté','Converti','Refusé'].includes(d.status) && (() => {
                      const sent = !!d.email_sent_at;
                      const count = Number(d.email_sent_count || 0);
                      const lastDate = d.email_sent_at
                        ? new Date(d.email_sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                        : null;
                      const tip = sent
                        ? `Relancer · Envoyé ${count}× — Dernier envoi : ${lastDate}${d.email_sent_to ? ` à ${d.email_sent_to}` : ''}`
                        : 'Envoyer en signature (PDF + lien)';
                      return (
                        <button
                          data-testid={`devis-send-${d.id}`}
                          onClick={() => handleSend(d)}
                          title={tip}
                          aria-label={tip}
                          className={`relative w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${sent ? 'bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC]' : 'hover:bg-[#EEF2FF] text-[#4F46E5]'}`}
                        >
                          {sent ? <MailCheck className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
                          {sent && count > 1 && (
                            <span
                              data-testid={`devis-send-count-${d.id}`}
                              className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#0F766E] text-white text-[9px] font-bold leading-none flex items-center justify-center"
                            >
                              {count > 9 ? '9+' : count}
                            </span>
                          )}
                        </button>
                      );
                    })()}
                    {d.status === 'Accepté' && (
                      <button
                        data-testid={`devis-convert-${d.id}`}
                        onClick={() => handleConvert(d)}
                        className="w-8 h-8 rounded-lg hover:bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center"
                        title="Convertir en facture"
                      >
                        <Receipt className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {d.status === 'Converti' && (() => {
                      // Wave 1u9e — lock only when the linked invoice is validated/sent/paid.
                      // If invoice is still Provisoire, allow re-validation flow (deep-link to it).
                      const invStatus = d.converted_invoice_status || 'Provisoire';
                      const isFinal = ['Validée', 'Émise', 'Payée'].includes(invStatus);
                      return (
                        <button
                          data-testid={`devis-converted-${d.id}`}
                          disabled
                          aria-disabled="true"
                          className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-not-allowed ${isFinal ? 'bg-[#E6FBF7] text-[#0F766E]' : 'bg-[#FEF3C7] text-[#92400E]'}`}
                          title={isFinal ? `Facture ${invStatus.toLowerCase()} — devis verrouillé` : 'Facture provisoire — à valider depuis Factures'}
                        >
                          <Receipt className="w-3.5 h-3.5" />
                        </button>
                      );
                    })()}
                    <button data-testid={`devis-pdf-${d.id}`} onClick={() => handleDownload(d)} className="w-8 h-8 rounded-lg hover:bg-[#F6F7FB] text-[#6B6C85] flex items-center justify-center" title="Télécharger PDF"><Download className="w-3.5 h-3.5" /></button>
                    {(() => {
                      // Wave 1u9e — edit/delete locked ONLY when invoice is validated.
                      const invStatus = d.converted_invoice_status || (d.status === 'Converti' ? 'Provisoire' : null);
                      const invoiceFinalised = ['Validée', 'Émise', 'Payée'].includes(invStatus || '');
                      return (
                        <RowActions
                          onView={() => setDetail(d)}
                          onEdit={invoiceFinalised ? undefined : () => setEditing(d)}
                          onDelete={invoiceFinalised ? undefined : () => setDeleting(d)}
                        />
                      );
                    })()}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <QuoteFormDialog
        open={create}
        onOpenChange={(o) => { setCreate(o); if (!o) setPrefillCreate(null); }}
        prefill={prefillCreate}
        onCreated={(d) => { setDevis((list) => [d, ...list]); refreshKpis(); }}
      />
      <QuoteFormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        quote={editing}
        onUpdated={(d) => {
          setDevis((prev) => prev.map((x) => x.id === d.id ? d : x));
          refreshKpis();
          if (detail?.id === d.id) {
            setDetail(d);
            api.get(`/quotes/${d.id}/versions`).then(({ data: v }) => setVersions(v)).catch(() => {});
          }
        }}
      />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer ce devis ?" description={deleting?.id} onConfirm={handleDelete} />
      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail?.id}
        subtitle={detail?.client}
        badge={detail && <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_STYLES[detail.status]}`}>{detail.status}</span>}
        rows={detail ? [
          { label: 'Client', value: detail.client },
          { label: 'Programme', value: detail.program || '—' },
          { label: 'Apprenants', value: (detail.learner_names || []).join(', ') || '—' },
          { label: 'Mode de facturation', value: detail.billing_mode === 'per_session' ? 'Par session' : detail.billing_mode === 'per_learner' ? 'Par apprenant' : '—' },
          { label: 'Sous-total HT', value: detail.subtotal ? `${Number(detail.subtotal).toLocaleString('fr-FR')} €` : `${(detail.amount_ht || detail.amount || 0).toLocaleString('fr-FR')} €` },
          { label: 'Remise', value: detail.discount ? `-${Number(detail.discount).toLocaleString('fr-FR')} €${detail.discount_type === 'percent' ? ` (${detail.discount_value}%)` : ''}` : '—' },
          { label: 'Total HT', value: `${(detail.amount_ht || 0).toLocaleString('fr-FR')} €` },
          { label: `TVA (${detail.tva_rate || 20}%)`, value: `${(detail.tva_amount || 0).toLocaleString('fr-FR')} €` },
          { label: 'Total TTC', value: `${(detail.amount || 0).toLocaleString('fr-FR')} €` },
          { label: 'Date', value: detail.date ? new Date(detail.date).toLocaleDateString('fr-FR') : '—' },
          { label: 'Valide jusqu\u2019au', value: detail.validUntil ? new Date(detail.validUntil).toLocaleDateString('fr-FR') : '—' },
          detail.signed_at ? { label: 'Signé le', value: `${new Date(detail.signed_at).toLocaleDateString('fr-FR')} par ${detail.signatory_name || '—'}` } : null,
          detail.email_sent_at
            ? {
                label: `Email envoyé${detail.email_sent_count > 1 ? ` · ${detail.email_sent_count}×` : ''}`,
                value: `${new Date(detail.email_sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}${detail.email_sent_to ? ` à ${detail.email_sent_to}` : ''}`,
              }
            : (detail.status && detail.status !== 'Brouillon' ? { label: 'Email envoyé', value: '— (non envoyé)' } : null),
          // **Ce que le client a recu comme conditions**, recopie a l'envoi :
          // c'est ce qu'on ressort s'il conteste une clause, et l'empreinte
          // dit quels octets sont partis — le fichier courant sera un autre.
          detail.cgv_remise
            ? {
                label: 'CGV remises',
                value: `${detail.cgv_remise.version ? `version du ${new Date(detail.cgv_remise.version).toLocaleDateString('fr-FR')}` : 'version inconnue'}`
                  + (detail.cgv_remise.pages ? ` · ${detail.cgv_remise.pages} page(s)` : '')
                  + (detail.cgv_remise.empreinte ? ` · ${String(detail.cgv_remise.empreinte).slice(0, 12)}` : ''),
              }
            : null,
        ].filter(Boolean) : []}
      >
        {detail && (
          <div className="mt-4 pt-4 border-t border-[#EFF0F6] space-y-3">
            {/* Pipeline visuel : Brouillon → Envoyé → Accepté → Converti → Facture Payée */}
            <div className="py-2">
              <StatusPipeline
                currentKey={detail.status === 'Converti' ? (detail.converted_invoice_status === 'Payée' ? 'Payée' : 'Converti') : detail.status}
                canceled={detail.status === 'Refusé'}
                steps={[
                  { key: 'Brouillon', label: 'Brouillon', ts: detail.created_at },
                  {
                    key: 'Envoyé',
                    label: 'Envoyé',
                    ts: detail.public_sign_token ? detail.email_sent_at || detail.created_at : null,
                    clickable: detail.status === 'Brouillon',
                    onClick: () => handleSend(detail),
                  },
                  {
                    key: 'Accepté',
                    label: 'Accepté',
                    ts: detail.signed_at,
                    clickable: detail.status === 'Envoyé' || detail.status === 'Brouillon',
                    onClick: () => setSigning(detail),
                  },
                  {
                    key: 'Converti',
                    label: 'Facturé',
                    ts: detail.converted_to_invoice_id ? detail.updated_at : null,
                    clickable: detail.status === 'Accepté',
                    onClick: () => handleConvert(detail),
                  },
                  { key: 'Payée', label: 'Payée', ts: detail.invoice_paid_at },
                ]}
              />
              {detail.status === 'Refusé' && (
                <div className="mt-2 text-[11.5px] text-[#B91C1C] font-semibold text-center">Devis refusé par le client</div>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={() => handleDownload(detail)} className="flex-1 h-10 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-1.5">
                <Download className="w-4 h-4" /> Télécharger PDF
              </button>
              <button onClick={() => handleSend(detail)} className="flex-1 h-10 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#EEF2FF]">
                <Send className="w-4 h-4" /> Envoyer au client
              </button>
            </div>
            {detail.status === 'Accepté' && (
              <button
                data-testid={`devis-convert-detail-${detail.id}`}
                onClick={() => handleConvert(detail)}
                className="w-full h-10 rounded-xl bg-[#E6FBF7] border border-[#A7F3D0] text-[#0F766E] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#CCF5EC]"
              >
                <Receipt className="w-4 h-4" /> Convertir en facture
              </button>
            )}
            {/* Pré-inscription / session creation button is intentionally NOT
                exposed inside the DetailSheet: the same action is already
                available from the row actions in the table (CalendarPlus
                icon) — duplicating it here cluttered the detail view. */}
            {detail.preinscription_at && (
              <button
                data-testid={`devis-open-session-detail-${detail.id}`}
                onClick={() => { const sid = detail.preinscription_session_id; if (sid) { setDetail(null); navigate(`/app/sessions?focus=${sid}`); } }}
                className="w-full h-10 rounded-xl border border-[#A7F3D0] bg-[#E6FBF7] text-[#0F766E] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#CCF5EC] transition-colors"
                title={`Pré-inscription créée le ${new Date(detail.preinscription_at).toLocaleDateString('fr-FR')}`}
              >
                <CheckCircle2 className="w-4 h-4" /> Ouvrir la session {detail.preinscription_session_id || ''}
              </button>
            )}
            {detail.status === 'Converti' && detail.converted_to_invoice_id && (
              <button
                data-testid={`devis-open-invoice-detail-${detail.id}`}
                onClick={() => { const iid = detail.converted_to_invoice_id; setDetail(null); navigate(`/app/factures?focus=${iid}`); }}
                className="w-full text-left text-[12px] text-[#0F766E] bg-[#E6FBF7] border border-[#A7F3D0] rounded-lg px-3 py-2 hover:bg-[#CCF5EC] transition-colors cursor-pointer"
                title="Ouvrir la facture"
              >
                ✓ Converti en facture <span className="font-mono font-semibold underline underline-offset-2">{detail.converted_to_invoice_id}</span>
              </button>
            )}

            {/* Convention de formation (per-quote) — visible for accepted/converted quotes linked to a session */}
            {(detail.status === 'Accepté' || detail.status === 'Converti') && (detail.session_id || detail.preinscription_session_id) && (
              <ConventionAction quote={detail} />
            )}

            {/* Signature actions */}
            {!['Accepté', 'Converti', 'Refusé'].includes(detail.status) && (
              <div className="flex gap-2 pt-1">
                <button data-testid={`devis-sign-detail-${detail.id}`} onClick={() => setSigning(detail)} className="flex-1 h-10 rounded-xl bg-[#EEF2FF] border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#EDE8FF]">
                  <PenLine className="w-4 h-4" /> Faire signer
                </button>
                <button data-testid={`devis-link-detail-${detail.id}`} onClick={() => handleCreateSignLink(detail)} className="flex-1 h-10 rounded-xl bg-white border border-[#EFF0F6] text-[#4A4B63] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#F6F7FB]">
                  <Link2 className="w-4 h-4" /> Lien client
                </button>
              </div>
            )}

            {/* Versions timeline */}
            <QuoteVersionsTimeline
              versions={versions}
              detail={detail}
              onDownloadPdf={handleDownloadVersionPdf}
              onRestore={handleRestoreVersion}
            />

            {/* History */}
            {(history?.audits?.length > 0 || history?.signatures?.length > 0) && (
              <div className="pt-3 border-t border-[#EFF0F6]" data-testid="devis-history">
                <div className="text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-2">Historique</div>
                <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
                  {(history.signatures || []).map((s) => (
                    <div key={s.id} className="flex items-start gap-2 text-[12px] p-2 rounded-lg bg-[#EEF2FF]">
                      <PenLine className="w-3.5 h-3.5 text-[#4F46E5] mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold text-[#1A1B2E]">Signature ({s.method === 'public_link' ? 'lien client' : 'admin'})</div>
                        <div className="text-[#6B6C85]">{s.signatory_name} · {new Date(s.signed_at).toLocaleString('fr-FR')}</div>
                      </div>
                    </div>
                  ))}
                  {(history.audits || []).map((a) => (
                    <div key={a.id || `${a.action}-${a.at}`} className="flex items-start gap-2 text-[11.5px] text-[#6B6C85] p-1.5">
                      <Clock className="w-3 h-3 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-[#4A4B63]">{a.action}</span> · {new Date(a.at).toLocaleString('fr-FR')} · {a.user_email || 'système'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DetailSheet>

      {/* Admin signature dialog */}
      <Dialog open={!!signing} onOpenChange={(o) => !o && setSigning(null)}>
        <DialogContent className="max-w-[520px]" data-testid="devis-sign-dialog">
          <DialogHeader>
            <DialogTitle>Faire signer le devis {signing?.id}</DialogTitle>
            <DialogDescription>
              Recueillez la signature du client (en personne ou sur cet appareil). Le devis passera en « Accepté ».
            </DialogDescription>
          </DialogHeader>
          {signing && (
            <SignaturePad
              initialName={signing.client || ''}
              onCancel={() => setSigning(null)}
              onSave={(dataUrl, name) => handleSignAdmin(signing, dataUrl, name)}
            />
          )}
        </DialogContent>
      </Dialog>

      <BulkActionsBar
        count={selected.size}
        onClear={() => setSelected(new Set())}
        onDelete={() => setBulkDeleteOpen(true)}
        onExport={bulkExport}
        extraActions={[
          { key: 'sign-link', label: 'Lien signature', icon: Link2, onClick: () => setBulkSignOpen(true), testId: 'bulk-sign-link-btn' },
        ]}
      />
      <ConfirmDialog
        open={bulkSignOpen}
        onOpenChange={setBulkSignOpen}
        title={`Envoyer ${selected.size} lien${selected.size > 1 ? 's' : ''} de signature ?`}
        description={`Un email contenant un lien unique de signature sera envoyé à chaque client disposant d'une adresse email. Les devis en statut « Brouillon » passeront automatiquement à « Envoyé ».`}
        confirmLabel="Envoyer les liens"
        onConfirm={runBulkSignatureLink}
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Supprimer ${selected.size} devis ?`}
        description={`Les devis signés (Accepté / Converti) seront ignorés. Les autres seront supprimés définitivement.`}
        confirmLabel={`Supprimer`}
        onConfirm={bulkDelete}
      />
      {/* Ce qui part avec le devis. La question ne se pose que lorsque le
          client a une étude de besoin : sans elle, l'envoi reste d'un seul
          clic, comme avant.

          Deux gestes, pas un : l'étude **faite** s'envoie en PDF — c'est une
          pièce, une trace de ce sur quoi le chiffrage repose ; celle qui
          **reste à faire** s'envoie en lien modifiable, pré-remplie de ce
          qu'on sait déjà. */}
      <Dialog open={!!aJoindre} onOpenChange={(o) => !o && setAJoindre(null)}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Joindre l&rsquo;étude de besoin ?</DialogTitle>
            <DialogDescription>
              Le devis en PDF et le programme de formation partent de toute façon.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(aJoindre?.etudes || []).map((e) => {
              const mode = aJoindre.modes[e.id] || null;
              const faite = e.status === 'filled';
              const poser = (v) => setAJoindre((a) => ({
                ...a, modes: { ...a.modes, [e.id]: a.modes[e.id] === v ? null : v },
              }));
              return (
                <div key={e.id} data-testid={`joindre-etude-${e.id}`}
                     className="p-3 rounded-xl border border-[#EFF0F6]">
                  <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">
                    {e.title}
                  </div>
                  <div className="text-[11.5px] text-[#8E8FA5] mb-2">
                    {faite
                      ? `remplie ${e.fill_mode === 'external' ? 'par le client' : 'par nous'}`
                      : 'pas encore remplie'}
                    {e.releve?.valide_sans_changement ? ' · validée sans changement' : ''}
                    {e.releve?.modifies?.length
                      ? ` · ${e.releve.modifies.length} réponse(s) corrigée(s)` : ''}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`etude-pdf-${e.id}`}
                      onClick={() => poser('pdf')}
                      disabled={!faite}
                      title={faite ? 'Joindre le PDF de l’étude'
                                   : "Elle n'est pas remplie : il n'y a pas de pièce à joindre"}
                      className="h-9 px-3 rounded-lg border text-[12.5px] font-semibold disabled:opacity-40"
                      style={mode === 'pdf'
                        ? { borderColor: '#4F46E5', backgroundColor: '#EEF2FF', color: '#4F46E5' }
                        : { borderColor: '#EFF0F6', color: '#4A4B63' }}
                    >
                      Joindre le PDF
                    </button>
                    <button
                      type="button"
                      data-testid={`etude-lien-${e.id}`}
                      onClick={() => poser('lien')}
                      title="Le client la remplit en ligne, pré-remplie de ce qu'on sait"
                      className="h-9 px-3 rounded-lg border text-[12.5px] font-semibold"
                      style={mode === 'lien'
                        ? { borderColor: '#4F46E5', backgroundColor: '#EEF2FF', color: '#4F46E5' }
                        : { borderColor: '#EFF0F6', color: '#4A4B63' }}
                    >
                      Envoyer le questionnaire à remplir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              onClick={() => {
                const d = aJoindre.devis;
                setAJoindre(null);
                handleSend({ ...d, _etudesDemandees: true, _etudes: null });
              }}
              className="h-9 px-4 rounded-lg bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#6B6C85] hover:bg-[#F6F7FB]"
            >
              Envoyer sans
            </button>
            <button
              data-testid="joindre-etude-valider"
              onClick={() => {
                const d = aJoindre.devis;
                const choisies = Object.entries(aJoindre.modes)
                  .filter(([, m]) => m)
                  .map(([id, m]) => ({ id, mode: m }));
                setAJoindre(null);
                handleSend({
                  ...d, _etudesDemandees: true,
                  _etudes: choisies.length ? choisies : null,
                });
              }}
              className="h-9 px-4 rounded-lg bg-[#4F46E5] text-white text-[13px] font-semibold hover:opacity-95"
            >
              Envoyer le devis
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

